// ================================================================
// auth.ts — MSAL Browser (Auth Code + PKCE)
//
// Client ID 和 Tenant ID 從 runtime config (localStorage) 讀取，
// 不需要在 build 時寫死，讓管理者透過 SetupWizard 自行設定。
// ================================================================

import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { getConfig } from './config';

let msalInstance: PublicClientApplication | null = null;

function buildMsalConfig(): Configuration {
  const cfg = getConfig();
  if (!cfg) throw new Error('Platform config not found. Please run setup wizard.');

  const authority = cfg.tenantId === 'common'
    ? 'https://login.microsoftonline.com/common'
    : `https://login.microsoftonline.com/${cfg.tenantId}`;

  return {
    auth: {
      clientId:    cfg.loginClientId,
      authority,
      redirectUri: cfg.redirectUri,
    },
    cache: {
      cacheLocation:          'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };
}

function getApiScopes(): string[] {
  const cfg = getConfig();
  if (!cfg) return [];
  return [`api://${cfg.loginClientId}/access_as_user`];
}

export async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    const config = buildMsalConfig();
    msalInstance = new PublicClientApplication(config);
    await msalInstance.initialize();

    // 處理從 Entra redirect 回來的結果（redirect flow 的關鍵）。
    // 若這次載入是登入導回，result 會帶 account；否則回 null。
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else if (!msalInstance.getActiveAccount()) {
      // 重整後 session 仍在 → 從快取補回 active account
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);
    }
  }
  return msalInstance;
}

/** 重設 MSAL instance（設定更新後呼叫，讓下次 getMsalInstance() 重新初始化）*/
export function resetMsalInstance(): void {
  msalInstance = null;
}

export async function login(): Promise<AccountInfo | null> {
  const msal = await getMsalInstance();
  // 改用 redirect flow：整頁導向 Entra，避免被瀏覽器彈窗封鎖（popup_window_error）。
  // 導向後本函式不會再返回；登入結果由 getMsalInstance() 的 handleRedirectPromise() 接住。
  await msal.loginRedirect({ scopes: getApiScopes() });
  return null;
}

export async function logout(): Promise<void> {
  const msal = await getMsalInstance();
  await msal.logoutRedirect();
}

export async function getAccessToken(): Promise<string | null> {
  const msal = await getMsalInstance();
  const account = msal.getActiveAccount();
  if (!account) return null;

  try {
    const result: AuthenticationResult = await msal.acquireTokenSilent({
      account,
      scopes: getApiScopes(),
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // silent 失敗 → 改用 redirect 取得互動式同意（同樣避開彈窗封鎖）
      await msal.acquireTokenRedirect({ scopes: getApiScopes() });
      return null; // 導向中，本次不會返回 token
    }
    console.error('[auth] Token acquisition failed:', err);
    return null;
  }
}

export async function getActiveAccount(): Promise<AccountInfo | null> {
  try {
    const msal = await getMsalInstance();
    return msal.getActiveAccount();
  } catch {
    // Config not yet set → no active account
    return null;
  }
}

export async function getUserRoles(): Promise<string[]> {
  try {
    const msal = await getMsalInstance();
    const account = msal.getActiveAccount();
    if (!account) return [];
    const claims = account.idTokenClaims as { roles?: string[] };
    return claims?.roles ?? [];
  } catch {
    return [];
  }
}
